import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { 
  ArrowLeft,
  User,
  Mail,
  Lock,
  Save,
  Loader2,
  CheckCircle,
  AlertTriangle,
  Eye,
  EyeOff
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import AnimatedLogo from "../components/AnimatedLogo";
import ThemeToggle from "../components/ThemeToggle";
import UserMenu from "../components/UserMenu";
import api from "../lib/api";

export default function AccountSettingsPage() {
  const { user, updateUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);
  
  const [formData, setFormData] = useState({
    contact_name: "",
    email: "",
    new_email: "",
    current_password: "",
    new_password: "",
    confirm_password: ""
  });

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        contact_name: user.contact_name || "",
        email: user.email || ""
      }));
    }
  }, [user]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await api.put("/users/me", {
        contact_name: formData.contact_name
      });
      if (updateUser) {
        updateUser(response.data);
      }
      toast.success("Nom mis à jour avec succès");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  const handleChangeEmail = async () => {
    if (!formData.new_email || formData.new_email === formData.email) {
      toast.error("Veuillez entrer une nouvelle adresse email");
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.new_email)) {
      toast.error("Adresse email invalide");
      return;
    }

    setSaving(true);
    try {
      await api.post("/users/me/change-email", {
        new_email: formData.new_email
      });
      setEmailVerificationSent(true);
      toast.success("Un email de vérification a été envoyé à votre nouvelle adresse");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du changement d'email");
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!formData.current_password || !formData.new_password) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }
    
    if (formData.new_password !== formData.confirm_password) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    
    if (formData.new_password.length < 6) {
      toast.error("Le mot de passe doit faire au moins 6 caractères");
      return;
    }

    setSaving(true);
    try {
      await api.post("/users/me/change-password", {
        current_password: formData.current_password,
        new_password: formData.new_password
      });
      toast.success("Mot de passe modifié avec succès");
      setFormData(prev => ({
        ...prev,
        current_password: "",
        new_password: "",
        confirm_password: ""
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du changement de mot de passe");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-4">
            <Link to="/settings">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <AnimatedLogo linkTo="/dashboard" showAnimation={true} size="small" />
          </div>
          <div className="flex items-center gap-1 sm:gap-3">
            <ThemeToggle />
            <UserMenu />
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-3" style={{ fontFamily: 'Outfit' }}>
            <User className="w-8 h-8 text-primary" />
            Mon compte
          </h1>
          <p className="text-muted-foreground mt-1">
            Gérez vos informations personnelles et votre sécurité
          </p>
        </div>

        <div className="space-y-6">
          {/* Profile Info */}
          <Card className="bg-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Informations du profil
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpdateProfile} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="contact_name">Nom complet</Label>
                  <Input
                    id="contact_name"
                    value={formData.contact_name}
                    onChange={(e) => handleChange("contact_name", e.target.value)}
                    placeholder="Votre nom"
                  />
                </div>
                <div className="flex justify-end">
                  <Button type="submit" disabled={saving} className="rounded-full">
                    {saving ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Enregistrer
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Email Change */}
          <Card className="bg-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary" />
                Adresse email
              </CardTitle>
              <CardDescription>
                Un email de vérification sera envoyé à la nouvelle adresse
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Email actuel</Label>
                <div className="flex items-center gap-2">
                  <Input
                    value={formData.email}
                    disabled
                    className="bg-muted"
                  />
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="new_email">Nouvelle adresse email</Label>
                <Input
                  id="new_email"
                  type="email"
                  value={formData.new_email}
                  onChange={(e) => handleChange("new_email", e.target.value)}
                  placeholder="nouvelle@email.com"
                />
              </div>

              {emailVerificationSent && (
                <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-blue-700 dark:text-blue-300">
                  <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                  <p className="text-sm">
                    Un email de vérification a été envoyé. Cliquez sur le lien dans l'email pour confirmer le changement.
                  </p>
                </div>
              )}

              <div className="flex justify-end">
                <Button 
                  type="button" 
                  onClick={handleChangeEmail} 
                  disabled={saving || !formData.new_email || formData.new_email === formData.email}
                  variant="outline"
                  className="rounded-full"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Mail className="w-4 h-4 mr-2" />
                  )}
                  Changer l'email
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Password Change */}
          <Card className="bg-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Lock className="w-5 h-5 text-primary" />
                Mot de passe
              </CardTitle>
              <CardDescription>
                Modifiez votre mot de passe de connexion
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current_password">Mot de passe actuel</Label>
                <div className="relative">
                  <Input
                    id="current_password"
                    type={showPassword ? "text" : "password"}
                    value={formData.current_password}
                    onChange={(e) => handleChange("current_password", e.target.value)}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="new_password">Nouveau mot de passe</Label>
                <Input
                  id="new_password"
                  type={showPassword ? "text" : "password"}
                  value={formData.new_password}
                  onChange={(e) => handleChange("new_password", e.target.value)}
                  placeholder="••••••••"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirm_password">Confirmer le mot de passe</Label>
                <Input
                  id="confirm_password"
                  type={showPassword ? "text" : "password"}
                  value={formData.confirm_password}
                  onChange={(e) => handleChange("confirm_password", e.target.value)}
                  placeholder="••••••••"
                />
              </div>

              <div className="flex justify-end">
                <Button 
                  type="button" 
                  onClick={handleChangePassword} 
                  disabled={saving || !formData.current_password || !formData.new_password}
                  variant="outline"
                  className="rounded-full"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Lock className="w-4 h-4 mr-2" />
                  )}
                  Changer le mot de passe
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
